import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classNames from 'classnames';

// components
import { PdpSharedShippingModalLoader } from '../PdpSharedModals/PdpSharedShippingModal/PdpSharedShippingModalLoader';
import { viewOptions, requestQuote, viewRequest } from '../messages/pdpShippingLinkMessages';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';
import { Link } from 'dibs-elements/exports/Link';

// styles
import styles from './PdpSharedShippingInfo.scss';

// helpers
import * as tracking from 'dibs-tracking';
import { getQueryParam } from '../../utils/uriUtils';
import get from 'lodash.get';

import { sharedLoginCheck } from '../../authentication/sharedLoginCheck';

import { isEmailOnly } from 'dibs-cookie-jar/src/helpers/emailOnlyHelpers';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

export class PdpSharedShippingInfoLinkComponent extends Component {
    constructor(props) {
        super(props);
        this.state = { modalOpen: false, hasLoggedIn: false, address: null };
        this.openShippingModal = this.openShippingModal.bind(this);
        this.closeShippingModal = this.closeShippingModal.bind(this);
        this.showAuthModal = this.showAuthModal.bind(this);
        this.showCompleteAccount = this.showCompleteAccount.bind(this);
    }

    componentDidMount() {
        const modalParam = getQueryParam(get(global, 'location.search'), 'modal');
        if (modalParam === 'shipping') {
            this.openShippingModal();
        }
    }

    showAuthModal({ onSuccess, onError } = {}) {
        sharedLoginCheck({
            onSuccess,
            onError,
            authOptions: {
                flow: authModalLoader.constants.REQUEST_SHIPPING_FLOW,
            },
        });
    }
    showCompleteAccount() {
        const { user } = this.props;
        const email = get(user, 'profile.email');
        authModalLoader
            .show({
                action: authModalLoader.constants.REQUEST_PASSWORD_EMAIL,
                flow: authModalLoader.constants.CREATE_PASSWORD_REQUEST_SHIPPING_MC_FLOW,
                email,
                relayEnvironment: this.props.relay.environment,
            })
            .then(({ userId }) => {
                if (userId) {
                    this.openShippingModal();
                }
            });
    }

    openShippingModal(status) {
        const { userId } = this.props;

        //handle login
        if (!userId && status === 'requestQuote') {
            this.showAuthModal({
                onSuccess: this.openShippingModal,
            });
        } else if (isEmailOnly(document.cookie) && status === 'requestQuote') {
            this.showCompleteAccount();
        } else {
            this.setState({ modalOpen: true }, () => {
                tracking.trackEvent(
                    {
                        isInteractiveEvent: true,
                        category: 'contact dealer',
                        action: 'contact dealer clicked',
                        label: 'request a shipping quote - pdp sidebar or details',
                    },
                    null
                );
            });
        }
    }

    closeShippingModal() {
        this.setState({ modalOpen: false });
    }

    render() {
        const {
            viewer,
            currency,
            userId,
            itemId,
            user,
            item,
            showQuoteDetails,
            quoteRequestStatus,
            relay,
            onSuccessfulQuoteRequest,
            displayInline,
            shipmentQuote,
        } = this.props;

        //enabler code
        const canRequestShippingQuotePermission = get(user, 'canRequestShippingQuotePermission');
        const onlyTradeRequestsQuote = false;
        const blockRequest = onlyTradeRequestsQuote && !canRequestShippingQuotePermission;

        const itemIsOnHold = get(this.props, 'item.itemIsOnHold');
        const hasPrivateListing = get(this.props, 'item.hasPrivateListing');
        const hasQuote = get(this.props, 'item.hasShipmentQuotes');
        const conversationId = get(viewer, 'buyerItemConversation.serviceId');
        const pendingRedirect = `/inbox/${conversationId}/`;
        const shouldHideLink = hasPrivateListing && quoteRequestStatus === 'requestQuote';
        const showViewRequestLink =
            quoteRequestStatus === 'customQuoteRequested' || shipmentQuote?.showQuoteAsPending;

        if (shouldHideLink) {
            return null;
        }
        // link cta
        let linkCta;
        if (showViewRequestLink) {
            linkCta = viewRequest;
        } else if (
            quoteRequestStatus === 'customQuoteProvided' ||
            quoteRequestStatus === 'viewQuotesNoCustom' ||
            ((itemIsOnHold || blockRequest) && quoteRequestStatus === 'viewQuotesNoCustom')
        ) {
            linkCta = viewOptions;
        } else {
            linkCta = requestQuote;
        }

        // link component
        let linkComponent;
        if (
            (itemIsOnHold || blockRequest) &&
            quoteRequestStatus !== 'viewQuotesNoCustom' &&
            quoteRequestStatus !== 'customQuoteRequested'
        ) {
            linkComponent = (
                <PdpSharedContactDealerButton
                    source="shipment-info-link"
                    user={user}
                    item={item}
                    viewer={viewer}
                />
            );
        } else if (showViewRequestLink) {
            linkComponent = (
                <Link href={pendingRedirect} className={styles.shippingLink} data-tn="view-request">
                    {linkCta}
                </Link>
            );
        } else {
            linkComponent = (
                <Link
                    onClick={() => {
                        this.openShippingModal(quoteRequestStatus);
                    }}
                    className={styles.shippingLink}
                    dataTn="pdp-shipping-modal-link"
                >
                    {linkCta}
                </Link>
            );
        }

        const containerClass = classNames(styles.shippingModalLink, {
            [styles.displayInline]: displayInline,
        });

        return (
            <div className={containerClass}>
                {linkComponent}
                <div>
                    <PdpSharedShippingModalLoader
                        currency={currency}
                        userId={userId}
                        itemId={itemId}
                        environment={relay.environment}
                        hasQuote={hasQuote}
                        showQuoteDetails={showQuoteDetails}
                        isOpened={this.state.modalOpen}
                        closeShippingModal={this.closeShippingModal}
                        trackingLocation="pdp sidebar"
                        showAuthModal={this.showAuthModal}
                        showCompleteAccount={this.showCompleteAccount}
                        openModal={this.openShippingModal}
                        onSuccess={onSuccessfulQuoteRequest}
                    />
                </div>
            </div>
        );
    }
}

PdpSharedShippingInfoLinkComponent.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    relay: PropTypes.object.isRequired,
    showQuoteDetails: PropTypes.bool.isRequired,
    quoteRequestStatus: PropTypes.string,
    onSuccessfulQuoteRequest: PropTypes.func,
    displayInline: PropTypes.bool,
};

PdpSharedShippingInfoLinkComponent.defaultProps = {
    onSuccessfulQuoteRequest: () => {},
};

export const PdpSharedShippingInfoLink = createFragmentContainer(
    PdpSharedShippingInfoLinkComponent,
    {
        viewer: graphql`
            fragment PdpSharedShippingInfoLink_viewer on Viewer {
                ...PdpSharedContactDealerButton_viewer
                buyerItemConversation(buyerId: $userId, itemId: $itemId) @include(if: $fetchUser) {
                    serviceId
                }
            }
        `,
        user: graphql`
            fragment PdpSharedShippingInfoLink_user on User {
                ...PdpSharedContactDealerButton_user
                canRequestShippingQuotePermission: hasPermission(name: "B_REQUEST_SHIPPING_QUOTE")
                profile {
                    email
                }
            }
        `,
        item: graphql`
            fragment PdpSharedShippingInfoLink_item on Item {
                ...PdpSharedContactDealerButton_item
                hasShipmentQuotes
                itemIsOnHold: isOnHold(buyerId: $userId)
                hasPrivateListing
                attributes {
                    leadTime {
                        value
                    }
                }
            }
        `,
        shipmentQuote: graphql`
            fragment PdpSharedShippingInfoLink_shipmentQuote on ShipmentQuote {
                showQuoteAsPending
            }
        `,
    }
);
